// @ts-strict-ignore
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PropertyNames, PropertyTemplates, PropertyTemplateVO } from '@project/components/article/model/vo/property-template-vo';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { PropertyTemplateService } from '../service/property-template.service';
import { PropertyDefTypes } from '../add-edit-property-def/property-def-types.enum';
import { Observable, of } from 'rxjs';
import { AdminToolService } from '../../../services/admintool.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AdmintoolComponentsPresenter } from '../../presenter/admintool-components.presenter';

@Component({
  standalone: false,
  selector: 'app-manage-properties-def',
  templateUrl: './manage-properties-def.component.html',
  styleUrls: ['./manage-properties-def.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagePropertiesDefComponent implements OnInit {
  public form: FormGroup;
  public PropertyTypes = PropertyDefTypes;
  public propertyNames: string[];
  public update = new EventEmitter<void>();

  private connectionProperties: PropertyTemplateVO[] = [];
  private behaviorProperties: PropertyTemplateVO[] = [];
  private plausiProperties: PropertyTemplateVO[] = [];
  private selectedPropertyType: string;

  constructor(
    private bsModalRef: BsModalRef,
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private alertHelper: AlertHelper,
    private propertyTemplateService: PropertyTemplateService,
    private adminToolService: AdminToolService,
    private admintoolComponentsPresenter: AdmintoolComponentsPresenter
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      propertyType: [PropertyDefTypes.BEHAVIOR],
    });
    this.getPropertiesTemplate();
  }

  public switchPropertyNames(): void {
    this.selectedPropertyType = this.form.controls.propertyType.value;

    if (this.selectedPropertyType === PropertyDefTypes.BEHAVIOR) {
      this.propertyNames = this.behaviorProperties
        .map((entry) => entry.propertyName)
        .sort((a: string, b: string) => {
          return a.localeCompare(b);
        });
    } else if (this.selectedPropertyType === PropertyDefTypes.PLAUSI) {
      this.propertyNames = this.plausiProperties
        .map((entry) => entry.propertyName)
        .sort((a: string, b: string) => {
          return a.localeCompare(b);
        });
    } else if (this.selectedPropertyType === PropertyDefTypes.CONNECTION) {
      this.propertyNames = this.connectionProperties
        .map((entry) => entry.propertyName)
        .sort((a: string, b: string) => {
          return a.localeCompare(b);
        });
    }
    this.cdRef.detectChanges();
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public delete(propertyName: string): void {
    this.isPropertyInUse(this.selectedPropertyType, propertyName).subscribe((response: boolean) => {
      if (response) {
        this.alertHelper.showError('managePropertiesDef.deletePropertyErrorTitle', 'managePropertiesDef.deleteMessage');
      } else {
        const property = this.getPropertyTemplateByName(propertyName);
        this.propertyTemplateService.deletePropertyTemplate(property).subscribe(() => {
          this.getPropertiesTemplate();
        });
      }
    });
  }

  public async openAddPropertyDialog(): Promise<void> {
    await this.admintoolComponentsPresenter.openAddPropertyDefManagementDialog(this.setPropertiesNamesDTO(), this.selectedPropertyType);
    this.getPropertiesTemplate();
  }

  public async openEditPropertyDialog(propertyName: string): Promise<void> {
    const property = this.getPropertyTemplateByName(propertyName);
    await this.admintoolComponentsPresenter.openEditPropertyDefManagementDialog(property, this.setPropertiesNamesDTO());
    this.getPropertiesTemplate();
  }

  private getPropertiesTemplate(): void {
    this.propertyTemplateService.getAllPropertyTemplates().subscribe((propertyTemplates: PropertyTemplates) => {
      this.connectionProperties = propertyTemplates.connectionProperties;
      this.behaviorProperties = propertyTemplates.behaviorProperties;
      this.plausiProperties = propertyTemplates.plausiProperties;

      this.switchPropertyNames();
    });
  }

  private getPropertyTemplateByName(propertyName: string): PropertyTemplateVO {
    let property: PropertyTemplateVO;

    if (this.selectedPropertyType === PropertyDefTypes.BEHAVIOR) {
      property = this.behaviorProperties.find((propertyTemplate: PropertyTemplateVO) => {
        return propertyTemplate.propertyName === propertyName;
      });
    } else if (this.selectedPropertyType === PropertyDefTypes.PLAUSI) {
      property = this.plausiProperties.find((propertyTemplate: PropertyTemplateVO) => {
        return propertyTemplate.propertyName === propertyName;
      });
    } else {
      property = this.connectionProperties.find((propertyTemplate: PropertyTemplateVO) => {
        return propertyTemplate.propertyName === propertyName;
      });
    }

    return property;
  }

  private setPropertiesNamesDTO(): PropertyNames {
    return {
      behaviorProperties: this.behaviorProperties.map((entry) => entry.propertyName.toLowerCase()),
      connectionProperties: this.connectionProperties.map((entry) => entry.propertyName.toLowerCase()),
      plausiProperties: this.plausiProperties.map((entry) => entry.propertyName.toLowerCase()),
    };
  }

  private isPropertyInUse(propertyType: string, propertyName: string): Observable<boolean> {
    if (this.adminToolService.isPropertyUsedByCurrentlyLoadedArticle(propertyType, propertyName)) {
      return of(true);
    }
    return this.propertyTemplateService.isPropertyInUse(propertyType, propertyName);
  }
}
